import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { listDocumentsHousesHouseIdDocumentsGetOptions } from '@/client/@tanstack/react-query.gen.ts';
import { useAuth } from '@/context/AuthContext.tsx';

/**
 * One array for every empty read, so `documents` keeps its identity while the query is in flight.
 *
 * An inline `= []` default mints a new array per render, which defeats this hook's own `useMemo` and
 * every memo downstream of it — the review pass rebuilds its visibility set and re-renders every
 * card on each render until the request lands.
 */
const NO_DOCUMENTS: never[] = [];

interface HouseDocumentsOptions {
	refetchInterval?: number | false;
}

/**
 * Fetches the house documents list.
 *
 * RecordStatusProvider owns document-pipeline polling and invalidates this
 * list when enrichment or KB processing completes, so document consumers do
 * not each start their own house-status poller.
 */
const useHouseDocuments = (houseId: number | null, options: HouseDocumentsOptions = {}) => {
	const { token } = useAuth();

	const enabled = !!houseId && !!token;

	const {
		data: documents = NO_DOCUMENTS,
		isLoading,
		isError,
		error,
		refetch,
		isFetching,
	} = useQuery({
		...listDocumentsHousesHouseIdDocumentsGetOptions({
			path: { house_id: houseId as number },
		}),
		enabled,
		staleTime: 1000 * 60 * 3,
		refetchInterval: options.refetchInterval ?? false,
		refetchIntervalInBackground: false,
	});
	// Before auth/house context has resolved the query is disabled, and React Query reports
	// `isLoading: false` with no data. Treat that as initial loading so Records never flashes its
	// genuine empty state before the first request can begin.
	const isInitiallyLoading = !enabled || isLoading;

	return useMemo(
		() => ({
			documents,
			isLoading: isInitiallyLoading,
			isError,
			error,
			isFetching,
			refetch,
			refetchDocuments: refetch,
		}),
		[documents, error, isError, isFetching, isInitiallyLoading, refetch],
	);
};

export default useHouseDocuments;
